import React, { useEffect, useState } from "react";
import "../../../pages/auth/CheckEmail.css";
import EyeIcon from "../../../images/icons/eye-icon.svg";
import EyeCrossed from "../../../images/icons/eye-crossed.svg";
import {
    otpVerification,
    registerUser,
} from "../../../../src/services/emailRegister/emailRegisterServices.js";
import { AES, enc } from "crypto-js";
import LoginImageMobile from "../../../images/login/lg-img-mob.png";
import LoginImage from "../../../images/login/lg-img.png";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ThreeDots } from "react-loader-spinner";
import { getBrandLogo, getBrandLogoAlt } from "../../../utils/brandLogo";

const PASSWORD_MIN = 8;
const COMPANY_MIN = 2;
const COMPANY_MAX = 50;

const Register = () => {
    const { email, otp } = useParams();
    const decodedEmail = decodeURIComponent(email.replace(/%2F/g, "/"));
    const decryptedBytes = AES.decrypt(decodedEmail, "encryptionKey");
    const deEmail = decryptedBytes.toString(enc.Utf8);
    const navigate = useNavigate();

    const [companyName, setCompanyName] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState({ companyName: "", password: "", confirmPassword: "" });

    const currentPort = window.location.port;
    const baseDomain = process.env.REACT_APP_BASE_DOMAIN || window.location.hostname;

    useEffect(() => {
        const checkUser = async () => {
            try {
                const result = await otpVerification(deEmail, otp);
                if (result !== true) {
                    toast.error("Invalid or expired OTP. Please verify your email again.", { position: "bottom-right", autoClose: 2000 });
                    navigate("/");
                }
            } catch {
                toast.error("Verification failed. Please try again.", { position: "bottom-right", autoClose: 2000 });
                navigate("/");
            }
        };
        checkUser();
    }, [deEmail, otp, navigate]);

    const validateCompanyName = (v) => {
        if (!v.trim()) return "Company name is required.";
        if (v.trim().length < COMPANY_MIN) return `At least ${COMPANY_MIN} characters required.`;
        if (v.trim().length > COMPANY_MAX) return `Max ${COMPANY_MAX} characters allowed.`;
        if (!/^[a-zA-Z0-9\s\-_.&]+$/.test(v.trim())) return "Only letters, numbers, spaces and - _ . & allowed.";
        return "";
    };

    const validatePassword = (v) => {
        if (!v) return "Password is required.";
        if (v.length < PASSWORD_MIN) return `At least ${PASSWORD_MIN} characters required.`;
        if (!/[A-Z]/.test(v)) return "Must include at least one uppercase letter.";
        if (!/[0-9]/.test(v)) return "Must include at least one number.";
        return "";
    };

    const validateConfirm = (v, pwd) => {
        if (!v) return "Please confirm your password.";
        if (v !== pwd) return "Passwords do not match.";
        return "";
    };

    const handleCompanyChange = (e) => {
        const v = e.target.value;
        setCompanyName(v);
        if (errors.companyName) setErrors((p) => ({ ...p, companyName: validateCompanyName(v) }));
    };

    const handlePasswordChange = (e) => {
        const v = e.target.value;
        setPassword(v);
        if (errors.password) setErrors((p) => ({ ...p, password: validatePassword(v) }));
        if (errors.confirmPassword && confirmPassword) setErrors((p) => ({ ...p, confirmPassword: validateConfirm(confirmPassword, v) }));
    };

    const handleConfirmChange = (e) => {
        const v = e.target.value;
        setConfirmPassword(v);
        if (errors.confirmPassword) setErrors((p) => ({ ...p, confirmPassword: validateConfirm(v, password) }));
    };

    const validateAll = () => {
        const e = {
            companyName: validateCompanyName(companyName),
            password: validatePassword(password),
            confirmPassword: validateConfirm(confirmPassword, password),
        };
        setErrors(e);
        return !e.companyName && !e.password && !e.confirmPassword;
    };

    const submitHandler = async () => {
        if (!validateAll()) return;
        setLoading(true);
        try {
            const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
            const result = await registerUser(deEmail, companyName.trim(), password, companyName.trim(), false, browserTimeZone);
            if (!result) throw new Error("No response from server");
            if (result.status === "success") {
                if (result.data && result.data.subDomain) {
                    const dest = currentPort === "3000"
                        ? `http://${result.data.subDomain}.${baseDomain}:3000`
                        : `https://${result.data.subDomain}.${baseDomain}`;
                    window.location.href = dest;
                } else {
                    toast.success(result.message || "Registration successful!", { position: "bottom-right", autoClose: 2000 });
                    setLoading(false);
                }
            } else {
                setLoading(false);
                toast.error(result.message || "Registration failed. Please try again.", { position: "bottom-right", autoClose: 2000 });
            }
        } catch (error) {
            setLoading(false);
            toast.error(error.response?.data?.message || error.message || "An error occurred. Please try again.", { position: "bottom-right", autoClose: 2000 });
        }
    };

    return (
        <div className="login-outer-forget">
            <div className="login-inner">
                <div className="top-logo-mobile">
                    <Link to="/"><img src={getBrandLogo()} alt={getBrandLogoAlt()} /></Link>
                </div>
                <div className="lg-lft">
                    <img src={LoginImage} alt="desktop" className="desktop-lg-img" fetchpriority="high" />
                    <img src={LoginImageMobile} alt="mobile" className="mobile-lg-img" />
                </div>
                <div className="lg-rgt">
                    <div className="top-logo">
                        <Link to="/"><img src={getBrandLogo()} alt={getBrandLogoAlt()} /></Link>
                    </div>
                    <div className="login-form-sec">
                        <form onSubmit={(e) => { e.preventDefault(); submitHandler(); }}>
                            <h3>Create your account</h3>

                            <div className="form-outer">
                                <label>Email</label>
                                <input type="email" value={deEmail} disabled />
                            </div>

                            <div className="form-outer">
                                <label>Company Name</label>
                                <input
                                    type="text"
                                    value={companyName}
                                    onChange={handleCompanyChange}
                                    placeholder="Your company name"
                                    className={errors.companyName ? "input-error" : ""}
                                    disabled={loading}
                                    maxLength={COMPANY_MAX}
                                />
                                {errors.companyName && <span className="field-error-msg">{errors.companyName}</span>}
                            </div>

                            <div className="form-outer">
                                <label>Password</label>
                                <div style={{ position: "relative", width: "100%" }}>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        value={password}
                                        onChange={handlePasswordChange}
                                        placeholder="Min 8 chars, 1 uppercase, 1 number"
                                        className={errors.password ? "input-error" : ""}
                                        disabled={loading}
                                        style={{ paddingRight: "50px" }}
                                    />
                                    <img
                                        src={showPassword ? EyeCrossed : EyeIcon}
                                        style={{ position: "absolute", top: "50%", right: "20px", transform: "translateY(-50%)", cursor: "pointer" }}
                                        alt="Toggle"
                                        onClick={() => setShowPassword(!showPassword)}
                                    />
                                </div>
                                {errors.password && <span className="field-error-msg">{errors.password}</span>}
                            </div>

                            <div className="form-outer">
                                <label>Confirm Password</label>
                                <div style={{ position: "relative", width: "100%" }}>
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        value={confirmPassword}
                                        onChange={handleConfirmChange}
                                        placeholder="Re-enter your password"
                                        className={errors.confirmPassword ? "input-error" : ""}
                                        disabled={loading}
                                        style={{ paddingRight: "50px" }}
                                    />
                                    <img
                                        src={showConfirmPassword ? EyeCrossed : EyeIcon}
                                        style={{ position: "absolute", top: "50%", right: "20px", transform: "translateY(-50%)", cursor: "pointer" }}
                                        alt="Toggle"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    />
                                </div>
                                {errors.confirmPassword && <span className="field-error-msg">{errors.confirmPassword}</span>}
                            </div>

                            <div className="reset-button" style={{ marginTop: "8px" }}>
                                <button type="submit" disabled={loading}>
                                    {loading ? (
                                        <div className="outter-load-table">
                                            <ThreeDots height="38" width="40" radius="9" color="white" ariaLabel="loading" visible={true} />
                                        </div>
                                    ) : (
                                        "Create Account"
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Register;
